import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMap } from "@vis.gl/react-google-maps";
import { MarkerClusterer, type Renderer, type Cluster, type ClusterStats } from "@googlemaps/markerclusterer";

type MarkerElement = google.maps.marker.AdvancedMarkerElement;

const clusterRenderer: Renderer = {
  render(cluster: Cluster, stats: ClusterStats, map: google.maps.Map) {
    const count = cluster.count;
    const isLarge = count > Math.max(10, stats.clusters.markers.mean);
    const color = isLarge ? "#D32F2F" : "#1976D2";
    const size = Math.min(24 + Math.log2(count) * 8, 56);

    const svg = `
      <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
        <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2}" fill="${color}" opacity="0.7"/>
        <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 3}" fill="${color}"/>
        <text x="${size / 2}" y="${size / 2}" text-anchor="middle" dominant-baseline="central"
          fill="white" font-size="${Math.max(11, size / 3)}" font-weight="bold" font-family="sans-serif">
          ${count}
        </text>
      </svg>`;

    const content = document.createElement("div");
    content.innerHTML = svg;
    content.style.cursor = "pointer";

    const marker = new google.maps.marker.AdvancedMarkerElement({
      position: cluster.position,
      content,
      zIndex: 1000 + count,
      map,
    });

    return marker;
  },
};

export function useClusteredMarkers(enabled: boolean) {
  const map = useMap();
  const [markers, setMarkers] = useState<Record<string, MarkerElement>>({});
  const markersRef = useRef<Record<string, MarkerElement>>({});

  // Keep ref in sync with state
  markersRef.current = markers;

  const clusterer = useMemo(() => {
    if (!map || !enabled) return null;
    return new MarkerClusterer({
      map,
      renderer: clusterRenderer,
    });
  }, [map, enabled]);

  // Sync markers dict to clusterer
  useEffect(() => {
    if (!clusterer) return;
    clusterer.clearMarkers();
    const markerArray = Object.values(markers);
    if (markerArray.length > 0) {
      clusterer.addMarkers(markerArray);
    }
  }, [clusterer, markers]);

  // Cleanup: restore markers to map when clusterer is removed/disabled
  useEffect(() => {
    if (!clusterer) return;
    return () => {
      const currentMap = clusterer.getMap() as google.maps.Map | null;
      const allMarkers = [...(clusterer as unknown as { markers: MarkerElement[] }).markers];
      clusterer.clearMarkers();
      clusterer.setMap(null);
      // MarkerClusterer hides individual markers (sets map=null) when
      // they're part of a cluster. Restore them so they're visible again.
      if (currentMap) {
        for (const m of allMarkers) {
          (m as MarkerElement).map = currentMap;
        }
      }
    };
  }, [clusterer]);

  const setMarkerRef = useCallback(
    (marker: MarkerElement | null, key: string) => {
      setMarkers((prev) => {
        if (marker) {
          if (prev[key] === marker) return prev;
          return { ...prev, [key]: marker };
        }
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    []
  );

  // Stable identity — reads from ref so it never changes
  const getMarker = useCallback(
    (key: string): MarkerElement | null => markersRef.current[key] ?? null,
    []
  );

  return { setMarkerRef, clusterer, getMarker };
}
